import React from "react"
import { Block } from "baseui/block"
import { useTranslation } from "react-i18next"
import { useEditor } from "@agenteresolve/react"
import useDesignEditorPages from "~/hooks/useDesignEditorScenes"
import useDesignEditorContext from "~/hooks/useDesignEditorContext"
import { checkUsage, reportUsage, USAGE_VIDEO_RENDER } from "~/utils/saas"
import { hfApiUrl, hfResolve } from "~/utils/hyperframes"

// Estágios do CLI -> chaves de tradução (namespace "editor", bloco "export").
const STAGE_KEYS: Record<string, string> = {
  queued: "preparing",
  compile: "compiling",
  extract_video: "extracting",
  process_audio: "processingAudio",
  start_browsers: "startingBrowsers",
  capture: "capturing",
  encode: "encoding",
  assemble: "assembling",
}

// CLI --resolution presets (aspect must match the composition; the scale is an
// integer device-pixel multiple).
const RESOLUTION_PRESETS = [
  { id: "landscape", label: "1080p · 1920×1080", width: 1920, height: 1080 },
  { id: "landscape-4k", label: "4K · 3840×2160", width: 3840, height: 2160 },
  { id: "square", label: "1080×1080", width: 1080, height: 1080 },
  { id: "square-4k", label: "2160×2160", width: 2160, height: 2160 },
  { id: "portrait", label: "1080p · 1080×1920", width: 1080, height: 1920 },
  { id: "portrait-4k", label: "4K · 2160×3840", width: 2160, height: 3840 },
]

const FORMAT_OPTIONS = [
  { id: "mp4", label: "MP4 (H.264)" },
  { id: "webm", label: "WebM (VP9)" },
  { id: "gif", label: "GIF" },
]

const QUALITY_OPTIONS = [
  { id: "draft", labelKey: "qualityDraft" },
  { id: "looks", labelKey: "qualityStandard" },
  { id: "delivery", labelKey: "qualityHigh" },
]

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "7px 8px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  fontSize: 12,
  background: "#fff",
}

const formatDuration = (seconds?: number) => {
  if (!seconds) return "--"
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
}

const buttonStyle: React.CSSProperties = {
  border: 0,
  borderRadius: 8,
  padding: "10px 14px",
  background: "#192bc2",
  color: "#fff",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
}

const linkStyle: React.CSSProperties = {
  display: "block",
  textAlign: "center",
  borderRadius: 8,
  padding: "9px 14px",
  border: "1px solid #d1d5db",
  color: "#0e0e52",
  fontSize: 13,
  fontWeight: 500,
  textDecoration: "none",
}

function Video() {
  const editor = useEditor()
  const pages = useDesignEditorPages()
  const { t } = useTranslation("editor")
  const { animationPreset } = useDesignEditorContext() as any
  const [phase, setPhase] = React.useState<"composing" | "ready" | "rendering" | "done" | "error">("composing")
  const [composition, setComposition] = React.useState<any>(null)
  const [job, setJob] = React.useState<any>(null)
  const [error, setError] = React.useState("")
  const [elapsed, setElapsed] = React.useState(0)
  const [scale, setScale] = React.useState(0.4)
  const [format, setFormat] = React.useState("mp4")
  const [quality, setQuality] = React.useState("looks")
  const [fps, setFps] = React.useState(30)
  const [resolution, setResolution] = React.useState("original")
  const containerRef = React.useRef<HTMLDivElement>(null)
  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null)

  const compose = React.useCallback(async () => {
    try {
      setPhase("composing")
      setError("")
      setJob(null)
      const current = editor.scene.exportToJSON() as any
      const list: any[] = pages && pages.length ? pages : [current]
      const payloadScenes = list.map((page: any) =>
        page && current && page.id === current.id
          ? { id: page.id, duration: page.duration, layers: current.layers }
          : { id: page?.id, duration: page?.duration, layers: page?.layers }
      )
      const res = await fetch(hfApiUrl("/compose/editor"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          frame: current.frame,
          scenes: payloadScenes,
          preset: animationPreset,
          fps: 30,
          assetBaseUrl: window.location.origin,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error || "compose failed")
      setComposition(data)
      setPhase("ready")
    } catch (e: any) {
      setError(String(e?.message || e))
      setPhase("error")
    }
  }, [editor, pages, animationPreset])

  React.useEffect(() => {
    if (editor) compose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor])

  React.useEffect(() => {
    const el = containerRef.current
    if (!el || !composition) return
    const recompute = () => {
      const pad = 40
      const w = el.clientWidth - pad * 2
      const h = el.clientHeight - pad * 2
      if (w <= 0 || h <= 0) return
      setScale(Math.max(Math.min(w / composition.width, h / composition.height), 0.05))
    }
    recompute()
    const observer = new ResizeObserver(recompute)
    observer.observe(el)
    return () => observer.disconnect()
  }, [composition])

  React.useEffect(() => {
    if (phase !== "rendering") return
    const started = Date.now()
    const timer = setInterval(() => setElapsed(Date.now() - started), 500)
    return () => clearInterval(timer)
  }, [phase])

  React.useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const startRender = React.useCallback(async () => {
    if (!composition) return
    try {
      setError("")
      const quota = await checkUsage(USAGE_VIDEO_RENDER)
      if (quota && !quota.allowed) {
        setError(t("export.limitReached"))
        setPhase("error")
        return
      }
      setJob(null)
      setElapsed(0)
      setPhase("rendering")
      const res = await fetch(hfApiUrl("/render"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: composition.id,
          format,
          fps,
          quality,
          ...(resolution !== "original" ? { resolution } : {}),
          async: true,
          workers: 4,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error || "render failed")
      const jobId = data.jobId
      pollRef.current = setInterval(async () => {
        try {
          const r = await fetch(hfApiUrl(`/render/jobs/${jobId}`), { cache: "no-store" })
          const j = await r.json()
          setJob(j)
          if (j.status === "done") {
            if (pollRef.current) clearInterval(pollRef.current)
            pollRef.current = null
            setPhase("done")
            reportUsage(USAGE_VIDEO_RENDER, {
              composition_id: j.compositionId,
              format: j.format,
              fps,
              ms: j.ms,
            })
          } else if (j.status === "error") {
            if (pollRef.current) clearInterval(pollRef.current)
            pollRef.current = null
            setError(j.error || "render failed")
            setPhase("error")
          }
        } catch {
          // keep polling
        }
      }, 1000)
    } catch (e: any) {
      setError(String(e?.message || e))
      setPhase("error")
    }
  }, [composition, format, fps, quality, resolution])

  const downloadDesign = React.useCallback(() => {
    if (!editor) return
    const current = editor.scene.exportToJSON() as any
    const list: any[] = pages && pages.length ? pages : [current]
    const design = {
      id: current?.id || "design",
      type: "VIDEO",
      name: current?.name || "Untitled Design",
      frame: current?.frame,
      scenes: list.map((page: any) => ({
        id: page.id,
        name: page.name || "",
        duration: page.duration ?? 5000,
        layers: page.id === current?.id ? current.layers : page.layers,
      })),
      metadata: {},
    }
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(design))
    const anchor = document.createElement("a")
    anchor.href = dataStr
    anchor.download = `${String(design.name || "design").replace(/\s+/g, "-").toLowerCase()}.json`
    anchor.click()
  }, [editor, pages])

  const resolutionOptions = React.useMemo(() => {
    if (!composition) return []
    const options = [{ id: "original", label: `Original · ${composition.width}×${composition.height}` }]
    for (const preset of RESOLUTION_PRESETS) {
      const scale = preset.width / composition.width
      if (!Number.isInteger(scale) || scale < 1) continue
      if (Math.abs(preset.height / composition.height - scale) > 0.001) continue
      options.push({ id: preset.id, label: preset.label })
    }
    return options
  }, [composition])

  const pct = job?.pct ?? 0
  const stageKey = job?.stage ? STAGE_KEYS[job.stage] : null
  const stageLabel = stageKey ? t(`export.${stageKey}`) : t("export.preparing")
  const frames = job?.done != null && job?.total != null ? `${job.done}/${job.total}` : ""
  const etaSeconds =
    phase === "rendering" && pct >= 5 ? Math.max(1, Math.round(((elapsed / 1000) * (100 - pct)) / pct)) : null

  return (
    <Block $style={{ flex: 1, display: "flex", height: "100%", background: "#0f1115" }}>
      <div
        ref={containerRef}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {composition && (
          <div
            style={{
              width: composition.width,
              height: composition.height,
              transform: `scale(${scale})`,
              transformOrigin: "center center",
              flex: "none",
              borderRadius: 8,
              overflow: "hidden",
              background: "#000",
              boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
            }}
          >
            <iframe
              key={composition.previewUrl}
              src={composition.previewUrl}
              style={{ width: composition.width, height: composition.height, border: 0, display: "block" }}
              title="hf-render-preview"
            />
          </div>
        )}
        {phase === "composing" && (
          <div style={{ position: "absolute", color: "#9aa0aa", fontSize: 14 }}>{t("export.preparing")}</div>
        )}
      </div>

      <div
        style={{
          width: 340,
          flex: "none",
          background: "#fff",
          borderLeft: "1px solid #e5e7eb",
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 14,
          overflowY: "auto",
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600 }}>{t("export.title")}</div>
        <div style={{ fontSize: 12, color: "#5b6484" }}>{t("export.description")}</div>

        {composition && (
          <div style={{ fontSize: 12, color: "#374151", display: "grid", gap: 4 }}>
            <span>
              {t("export.duration")}: {formatDuration(composition.duration)}
            </span>
            <span>
              {t("export.size")}: {composition.width}×{composition.height}
            </span>
          </div>
        )}

        {(phase === "ready" || phase === "done") && (
          <div style={{ display: "grid", gap: 8 }}>
            <label style={{ fontSize: 11, color: "#5b6484", display: "grid", gap: 3 }}>
              {t("export.format")}
              <select value={format} onChange={(e) => setFormat(e.target.value)} style={selectStyle}>
                {FORMAT_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <label style={{ fontSize: 11, color: "#5b6484", display: "grid", gap: 3 }}>
                {t("export.quality")}
                <select value={quality} onChange={(e) => setQuality(e.target.value)} style={selectStyle}>
                  {QUALITY_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {t(`export.${option.labelKey}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: 11, color: "#5b6484", display: "grid", gap: 3 }}>
                FPS
                <select value={fps} onChange={(e) => setFps(Number(e.target.value))} style={selectStyle}>
                  <option value={30}>30</option>
                  <option value={60}>60</option>
                </select>
              </label>
            </div>
            {resolutionOptions.length > 1 && (
              <label style={{ fontSize: 11, color: "#5b6484", display: "grid", gap: 3 }}>
                {t("export.resolution")}
                <select value={resolution} onChange={(e) => setResolution(e.target.value)} style={selectStyle}>
                  {resolutionOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <button onClick={startRender} style={buttonStyle}>
              {phase === "done" ? t("export.renderAgain") : t("export.render", { format: format.toUpperCase() })}
            </button>
          </div>
        )}

        {phase === "rendering" && (
          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ height: 8, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
              <div
                style={{
                  width: `${pct}%`,
                  height: "100%",
                  background: "#192bc2",
                  transition: "width .3s",
                }}
              />
            </div>
            <div style={{ fontSize: 12, color: "#374151" }}>
              {job?.waiting ? t("export.queued") : stageLabel}
              {frames ? ` · ${frames}` : ""} · {Math.round(pct)}%
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>
              {t("export.elapsed", { seconds: Math.round(elapsed / 1000) })}
              {etaSeconds ? ` · ${t("export.remaining", { seconds: etaSeconds })}` : ""} —{" "}
              {t("export.keepOpen")}
            </div>
            {job?.logs && (
              <pre
                style={{
                  fontSize: 10,
                  maxHeight: 96,
                  overflow: "hidden",
                  background: "#0f1115",
                  color: "#d1d5db",
                  padding: 8,
                  borderRadius: 6,
                  whiteSpace: "pre-wrap",
                }}
              >
                {String(job.logs).split("\n").filter(Boolean).slice(-3).join("\n").slice(-400)}
              </pre>
            )}
          </div>
        )}

        {phase === "done" && job?.url && (
          <div style={{ display: "grid", gap: 10 }}>
            <video src={job.url} controls style={{ width: "100%", borderRadius: 8, background: "#000" }} />
            <a href={job.url} download style={linkStyle}>
              {t("export.download", { format: String(job.format || "mp4").toUpperCase() })}
            </a>
            <a href={hfResolve(`/watch/${job.compositionId}/`)} target="_blank" rel="noreferrer" style={linkStyle}>
              {t("export.openWatch")}
            </a>
          </div>
        )}

        {phase === "error" && (
          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ fontSize: 13, color: "#b3261e" }}>{error}</div>
            {job?.logs && (
              <pre
                style={{
                  fontSize: 10,
                  maxHeight: 220,
                  overflow: "auto",
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  padding: 8,
                  borderRadius: 6,
                  whiteSpace: "pre-wrap",
                }}
              >
                {String(job.logs).slice(-2000)}
              </pre>
            )}
            <button onClick={composition ? startRender : compose} style={buttonStyle}>
              {t("export.retry")}
            </button>
          </div>
        )}

        <div style={{ marginTop: "auto", display: "grid", gap: 8, paddingTop: 12 }}>
          <button
            onClick={downloadDesign}
            style={{ ...linkStyle, background: "#fff", cursor: "pointer" }}
            type="button"
          >
            {t("export.downloadDesign")}
          </button>
          {composition && (
            <a href={hfResolve(`/watch/${composition.id}/`)} target="_blank" rel="noreferrer" style={{ ...linkStyle, fontSize: 12 }}>
              {t("export.openWatch")}
            </a>
          )}
        </div>
      </div>
    </Block>
  )
}

export default Video
